import React, { useState } from "react";
import { apiClient, formatApiErrorDetail } from "../../lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Clock, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * TaskStatusSheet — admin control to change a task's status.
 * Statuses:
 *   Pending     → "todo"
 *   In Progress → "in_progress"
 *   Completed   → "completed"
 *   Cancelled   → "cancelled"
 */
const OPTIONS = [
  { v: "todo", label: "Pending", Icon: Clock, tone: "bg-slate-100 text-slate-700 hover:bg-slate-200", active: "bg-slate-700 text-white" },
  { v: "in_progress", label: "In Progress", Icon: Loader2, tone: "bg-amber-50 text-amber-700 hover:bg-amber-100", active: "bg-amber-500 text-white" },
  { v: "completed", label: "Completed", Icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-700 hover:bg-emerald-100", active: "bg-emerald-600 text-white" },
  { v: "cancelled", label: "Cancelled", Icon: XCircle, tone: "bg-red-50 text-red-700 hover:bg-red-100", active: "bg-red-600 text-white" },
];

export function TaskStatusSheet({ task, open, onOpenChange, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const current = (task?.status || "todo").toLowerCase();

  const setStatus = async (status) => {
    if (!task) return;
    setBusy(true);
    try {
      const { data } = await apiClient.put(`/tasks/${task.id}`, { status });
      toast.success(`Marked ${OPTIONS.find((o) => o.v === status)?.label || status}`);
      onUpdated && onUpdated(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm rounded-3xl" data-testid="task-status-sheet">
        <DialogHeader>
          <DialogTitle className="text-base">Update task status</DialogTitle>
          <DialogDescription className="text-xs line-clamp-2">
            {task?.title || "Task"}
          </DialogDescription>
        </DialogHeader>
        {task?.employee_note && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 text-xs text-indigo-900" data-testid="task-employee-note">
            <span className="font-bold uppercase tracking-wider text-[10px] text-indigo-600 block mb-1">Note from employee</span>
            {task.employee_note}
          </div>
        )}
        <div className="grid grid-cols-2 gap-2 pt-2">
          {OPTIONS.map((o) => {
            const on = current === o.v;
            return (
              <button
                key={o.v}
                onClick={() => setStatus(o.v)}
                disabled={busy}
                data-testid={`task-status-${o.v}`}
                data-current={on ? "true" : "false"}
                className={`h-14 rounded-2xl text-sm font-bold transition flex items-center justify-center gap-1.5 disabled:opacity-50 ${on ? o.active : o.tone}`}
              >
                <o.Icon className={`w-4 h-4 ${o.v === "in_progress" && (on || busy) ? "animate-spin" : ""}`} strokeWidth={2.4} />
                {o.label}
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
